<template>
  <div class="page-container">
    <PageHeader title="操作日志"></PageHeader>

    <SearchPanel>
      <el-input v-model.trim="query.userName" placeholder="操作人" size="small" clearable></el-input>
      <el-input v-model.trim="query.logUserId" placeholder="用户ID" size="small" clearable @keyup.enter.native="handleSearch"></el-input>
      <el-input v-model.trim="query.content" placeholder="接口地址" size="small" clearable></el-input>
      <el-select v-model="query.logType" clearable placeholder="操作类型" size="small" @change="handleSearch">
        <el-option v-for="item in logTypeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
      </el-select>
      <el-select v-model="query.logPlatform" clearable placeholder="登录端" size="small" @change="handleSearch">
        <el-option label="PC" :value="1"></el-option>
        <el-option label="微信" :value="2"></el-option>
      </el-select>
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 30, 50]"
        :total="total"
        row-id-key="logId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
      >
        <template #cell-logType="{ row }">
          <el-tag size="mini" :type="getLogTypeTag(row.logType)">{{ getLogTypeLabel(row.logType) }}</el-tag>
        </template>
        <template #cell-logPlatform="{ row }">
          {{ row.logPlatform === 2 ? '微信' : 'PC' }}
        </template>
        <template #cell-logRequestTime="{ row }">
          <span :style="{ color: row.logRequestTime > 1000 ? '#ef4444' : '' }">{{ row.logRequestTime }}ms</span>
        </template>
      </aj-table>
      <EmptyState v-if="!loading && list.length === 0" title="暂无日志" icon="el-icon-document-copy"></EmptyState>
    </TablePanel>
  </div>
</template>

<script>
import { getSysLogList } from '@/api/log'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

// 操作类型选项（补全：新增"其他"和"导出"）
const LOG_TYPE_OPTIONS = [
  { label: '其他', value: 0, tag: 'info' },
  { label: '查询', value: 1, tag: '' },
  { label: '新增', value: 2, tag: 'success' },
  { label: '编辑', value: 3, tag: 'warning' },
  { label: '删除', value: 4, tag: 'danger' },
  { label: '导出', value: 5, tag: 'info' }
]

// 表格列（补全：平台、请求方式、请求耗时、携带参数、用户ID）
const LOG_COLUMNS = [
  { type: 'index', label: '序号', width: 60 },
  { prop: 'logPlatform', label: '平台', width: 65 },
  { prop: 'logUserName', label: '操作人', width: 100 },
  { prop: 'logType', label: '操作类型', width: 80 },
  { prop: 'logApi', label: '接口名', minWidth: 160 },
  { prop: 'logPath', label: '接口地址', minWidth: 180 },
  { prop: 'description', label: '接口具体类型', minWidth: 140 },
  { prop: 'logMethod', label: '请求方式', width: 80 },
  { prop: 'logIp', label: 'IP地址', width: 120 },
  { prop: 'logRequestTime', label: '耗时', width: 85 },
  { prop: 'logParams', label: '携带参数', minWidth: 200 },
  { prop: 'logUserId', label: '用户ID', width: 80 },
  { prop: 'logTime', label: '操作时间', width: 150 }
]

export default {
  name: 'SystemLogPage',
  components: { AjTable, EmptyState, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      total: 0,
      list: [],
      columns: LOG_COLUMNS,
      logTypeOptions: LOG_TYPE_OPTIONS,
      query: { userName: '', logUserId: '', content: '', logType: '', logPlatform: '', currentPage: 1, pageSize: 10 }
    }
  },
  created () { this.fetchList() },
  methods: {
    buildQueryParams () {
      return {
        logUserName: this.query.userName || undefined,
        logUserId: this.query.logUserId || undefined,
        logApi: this.query.content || undefined,
        logType: this.query.logType !== '' && this.query.logType !== null ? this.query.logType : undefined,
        logPlatform: this.query.logPlatform || undefined,
        currentPage: this.query.currentPage,
        pageSize: this.query.pageSize
      }
    },
    async fetchList () {
      this.loading = true
      try {
        const res = await getSysLogList(this.buildQueryParams())
        this.list = Array.isArray(res.data) ? res.data : []
        this.total = Number(res.total || 0)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },
    getLogTypeLabel (value) {
      const target = this.logTypeOptions.find(item => item.value === value)
      return target ? target.label : '其他'
    },
    getLogTypeTag (value) {
      const target = this.logTypeOptions.find(item => item.value === value)
      return target ? target.tag : 'info'
    },
    resetQuery () {
      this.query = { userName: '', logUserId: '', content: '', logType: '', logPlatform: '', currentPage: 1, pageSize: 10 }
      this.fetchList()
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
</style>
